import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Badge, Box, CircularProgress, Collapse } from '@mui/material';
import { styled } from '@mui/system';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '../../atoms';
import PortfolioApprovalItem from '../../molecules/PortfolioApprovalItem';

export type ApprovalStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export interface Approval {
    id: string;
    date: string;
    submitter: string;
    userId: string;
    tenant: string;
    property: string;
    fileName?: string;
    fileId?: string;
    fundTypes?: string;
    portfolioType?: string;
    fromUser?: string;
    fromUserEmail?: string;
    fromUserName?: string;
    toUser?: string;
    toUserEmail?: string;
    toUserName?: string;
    workflowType?: string;
    status: ApprovalStatus | string;
}

interface PortfolioApprovalsProps {
    pendingApprovals: Approval[] | [];
    open: boolean;
    onToggle: () => void;
    // eslint-disable-next-line
    onApprove: (id: string) => void;
    // eslint-disable-next-line
    onReject: (id: string) => void;
    isLoading?: boolean;
    actionLoading?: { [id: string]: boolean };
    hasExpandableDetails?: boolean;
    // eslint-disable-next-line
    summaryComponent?: (item: any) => React.ReactNode | null;
    // eslint-disable-next-line
    detailsComponent?: (item: any) => React.ReactNode | null;
}

const Container = styled(Box)(() => ({
    borderRadius: 16,
    width: '100%'
}));

interface HeaderProps {
    collapsed: boolean;
}

const Header = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'collapsed'
})<HeaderProps>(({ collapsed, theme }) => ({
    display: 'flex',
    padding: theme.spacing(2.5),
    cursor: 'pointer',
    borderBottom: collapsed ? 'none' : '1px solid #e5e5e5'
}));

const PortfolioApprovalsContainer = styled(Box)(() => ({
    minHeight: '88px'
}));

const EmptyApprovalsContainer = styled(Box)(({ theme }) => ({
    minHeight: '140px',
    padding: theme.spacing(2.5),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center'
}));

const PortfolioApprovals: React.FC<PortfolioApprovalsProps> = ({
    pendingApprovals,
    open,
    onToggle,
    isLoading = false,
    hasExpandableDetails = false,
    summaryComponent,
    detailsComponent = null
}) => {
    const { t } = useTranslation();
    const [expandedId, setExpandedId] = React.useState<string | null>(null);

    const handleExpand = (panelId: string) => () => {
        setExpandedId(panelId === expandedId ? null : panelId);
    };

    return (
        <Container>
            <Header onClick={onToggle} collapsed={open}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ExpandMoreIcon
                        sx={{
                            transition: 'transform 0.2s',
                            transform: !open ? 'rotate(0deg)' : 'rotate(-90deg)',
                            fontSize: 24,
                            color: '#222'
                        }}
                    />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {t('LABELS_CHECK_MAKER_PENDING_APPROVALS')}
                    </Typography>
                </Box>
                {isLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', ml: 3, mt: 0.5, height: 24 }}>
                        <CircularProgress size={12} sx={{ display: 'block', m: 0 }} />
                    </Box>
                ) : (
                    <Badge
                        badgeContent={pendingApprovals.length}
                        color="error"
                        sx={{ ml: 3, mt: 0.5, transform: 'scale(1) translate(0%, 50%) !important' }}
                    />
                )}
            </Header>
            <Collapse in={!open}>
                <PortfolioApprovalsContainer>
                    {isLoading ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight={120}>
                            <CircularProgress />
                        </Box>
                    ) : pendingApprovals.length === 0 ? (
                        <EmptyApprovalsContainer>
                            <AccessTimeIcon sx={{ color: '#99a1af', width: '1.2em', height: '1.2em' }} />
                            <Typography variant="body2" sx={{ padding: '16px 0' }}>
                                {t('LABELS_CHECK_MAKER_NO_PENDING_APPROVALS')}
                            </Typography>
                        </EmptyApprovalsContainer>
                    ) : (
                        pendingApprovals.map((item) => (
                            <PortfolioApprovalItem
                                key={item.id}
                                summaryComponent={summaryComponent ? summaryComponent(item) : null}
                                detailsComponent={detailsComponent ? detailsComponent(item) : null}
                                hasDetails={hasExpandableDetails}
                                expanded={expandedId === item.id}
                                onExpand={handleExpand(item.id)}
                            />
                        ))
                    )}
                </PortfolioApprovalsContainer>
            </Collapse>
        </Container>
    );
};

export default PortfolioApprovals;
